import { useRouter } from "next/router"
import { useCallback, useMemo, useState } from "react"
import { useLocalStorage } from "react-use"
import _ from "lodash"
import routes from "routes"
import useSWR from "swr"

import { GenreCategories } from "src/models/genre/GenreCategories"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import HTMLHead from "src/components/utils/HTMLHead"
import { fetchGenres } from "../../models/genre/fetchGenres"

const STORAGE_GENRE_KEY = "tieupSearch_genreSearchParams"

const Genres: React.FC = () => {
  const [searchText, setSearchText] = useState("")

  // apiから取得したジャンル一覧
  const { data, error } = useSWR("/v1/genre", fetchGenres)
  if (error) {
    // eslint-disable-next-line no-console
    console.log(error)
  }
  const genres = useMemo(() => {
    return data || []
  }, [data])

  // 選択したジャンル一覧
  const [selectedGenres, setSelectedGenres] = useLocalStorage<number[]>(
    STORAGE_GENRE_KEY,
    [],
  )

  const genreCategories: GenreCategories[] = ["分類", "傾向", "場所", "特殊"]

  const router = useRouter()

  const filterGenre = useCallback(
    (name: GenreCategories) => {
      return genres.filter((g) => g.category === name)
    },
    [genres],
  )

  /** 送信処理 */
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    router.push({
      pathname: routes.tieups(),
      query: {
        q: searchText ? searchText : null,
        g: selectedGenres,
      },
    })
  }

  // ジャンルIDを選択
  const toggleGenre = useCallback(
    (gid: number) => {
      let newGenres = selectedGenres ?? []
      if (selectedGenres && selectedGenres.includes(gid)) {
        // すでに含んでいたら削除
        newGenres = _.pull(newGenres, gid)
      } else {
        // まだ入ってなかったら登録
        newGenres.push(gid)
      }
      setSelectedGenres(newGenres)
    },
    [selectedGenres, setSelectedGenres],
  )

  /** 検索条件をクリア */
  const onClear = useCallback(() => {
    // 選択中のジャンルを削除
    setSelectedGenres([])
    // 検索文字の削除
    setSearchText("")
  }, [setSelectedGenres])

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="search/genres" />
      <form onSubmit={onSubmit}>
        <main>
          <div className="p-search-header">
            <div className="p-searchForm">
              <div className="p-searchForm-inner">
                <div className="p-searchForm-freeword">
                  <span className="p-searchForm-textfield c-search-textfield">
                    <input
                      className="c-textfield-primary"
                      type="text"
                      placeholder="フリーワード検索"
                      value={searchText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchText(e.target.value)
                      }}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="l-single -medium">
            <header className="p-searchDetail-header">
              <h1 className="p-searchDetail-title">ジャンルを選択</h1>
              <p className="p-searchDetail-note">※複数選択可</p>
            </header>
            <div className="p-genreList">
              {!!genres &&
                genreCategories.map((category) => {
                  return (
                    <div key={category} className="p-genreList-item">
                      <ul className="p-checkbox-list">
                        {filterGenre(category).map((g) => {
                          return (
                            <li key={g.id}>
                              <label className="c-checkbox-primary">
                                <input
                                  className="c-checkbox-primary-input"
                                  type="checkbox"
                                  value={g.id}
                                  checked={
                                    selectedGenres &&
                                    selectedGenres.includes(g.id)
                                  }
                                  onChange={(e) => {
                                    toggleGenre(parseInt(e.target.value, 10))
                                  }}
                                />
                                <span className="c-checkbox-primary-text">
                                  {g.name}
                                </span>
                              </label>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                })}
            </div>
            <div className="p-searchDetail-action">
              <div className="p-searchDetail-clear">
                <button
                  className="p-searchDetail-clear-button c-button-secondary -outline -full"
                  onClick={() => {
                    onClear()
                  }}
                  type="button"
                >
                  クリア
                </button>
              </div>
              <div className="p-searchDetail-submit">
                <button className="p-searchDetail-submit-button c-button-secondary -full">
                  検索する
                </button>
              </div>
            </div>
          </div>
        </main>
      </form>
    </DefaultLayoutForReader>
  )
}

export default Genres
